import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';
import {FaQrcode,FaWhatsapp,FaEdit} from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiMail,FiPhone,FiMenu,FiX} = FiIcons;

const Contact = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-red-600 selection:text-white">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center transform -rotate-6 shadow-xl shadow-red-900/40 hover:rotate-0 transition-transform duration-300">
              <FaQrcode className="text-white text-2xl" />
            </div>
            <span className="font-bold text-2xl tracking-tight">
              Connect<span className="text-red-500">Kard</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-10 text-sm font-semibold text-zinc-400">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <Link to="/features" className="hover:text-white transition-colors duration-300">Features</Link>
            <Link to="/how-it-works" className="hover:text-white transition-colors duration-300">How It Works</Link>
            <Link to="/contact" className="text-white transition-colors duration-300">Contact</Link>
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop Edit Card Button */}
            <Link to="/editqr" className="hidden sm:block">
              <Button variant="outline" className="px-6 py-2.5 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                <SafeIcon icon={FaEdit} />
                Edit Card
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:text-red-500 transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-white/10 animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors"
              >
                Home
              </Link>
              <Link 
                to="/features" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors"
              >
                Features
              </Link>
              <Link 
                to="/how-it-works" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors"
              >
                How It Works
              </Link>
              <Link 
                to="/contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white font-semibold py-2 transition-colors"
              >
                Contact
              </Link>
              <Link to="/editqr" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full py-3 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                  <SafeIcon icon={FaEdit} />
                  Edit Card
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-black to-black" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6 mb-16 animate-in fade-in slide-in-from-bottom duration-700">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight">
              Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">Touch</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              Have questions? We're here to help you get started with ConnectKard
            </p>
          </div>

          {/* Contact Cards Grid */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-12 animate-in fade-in slide-in-from-bottom duration-700 delay-200">
              {/* WhatsApp Card */}
              <a 
                href="https://wa.me/917796983999" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 hover:border-green-500/50 transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-500 transition-all duration-300">
                    <SafeIcon icon={FaWhatsapp} className="text-3xl text-green-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">WhatsApp</h3>
                  <p className="text-zinc-400 text-sm mb-4">Chat with us instantly</p>
                  <p className="text-green-500 font-bold">+91-77-969-83-999</p>
                </div>
              </a>

              {/* Email Card */}
              <a 
                href="mailto:support@connectkard.com"
                className="group"
              >
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-500 transition-all duration-300">
                    <SafeIcon icon={FiMail} className="text-3xl text-red-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Email</h3>
                  <p className="text-zinc-400 text-sm mb-4">Send us a message</p>
                  <p className="text-red-500 font-bold break-all">support@connectkard.com</p>
                </div>
              </a>

              {/* Phone Card */}
              <a 
                href="tel:+917796983999"
                className="group"
              >
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-all duration-300">
                    <SafeIcon icon={FiPhone} className="text-3xl text-blue-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Phone</h3>
                  <p className="text-zinc-400 text-sm mb-4">Call us directly</p>
                  <p className="text-blue-500 font-bold">+91-77-969-83-999</p>
                </div>
              </a>
            </div>

            {/* Business Hours */}
            <div className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-8 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-300 mb-12">
              <h3 className="text-xl font-bold mb-4">Business Hours</h3>
              <div className="space-y-2 text-zinc-400">
                <p className="flex items-center justify-center gap-2">
                  <span className="text-green-500">●</span>
                  <span>Monday - Saturday: 9:00 AM - 8:00 PM</span>
                </p>
                <p className="flex items-center justify-center gap-2">
                  <span className="text-yellow-500">●</span>
                  <span>Sunday: 10:00 AM - 6:00 PM</span>
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-red-950/30 to-zinc-950 border border-red-500/20 rounded-2xl p-8 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-400">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-zinc-400 mb-6">
                Order your ConnectKard today or edit your existing card
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://wa.me/917796983999?text=I%20want%20to%20buy%20ConnectKard" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button className="w-full sm:w-auto px-8 py-3">
                    <SafeIcon icon={FaWhatsapp} />
                    Order Now
                  </Button>
                </a>
                <Link to="/editqr" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto px-8 py-3">
                    <SafeIcon icon={FaEdit} />
                    Edit Card
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 bg-zinc-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3 hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                <FaQrcode className="text-white text-xl" />
              </div>
              <span className="font-bold text-xl">
                Connect<span className="text-red-500">Kard</span>
              </span>
            </div>
            
            <p className="text-zinc-500 text-sm">
              © 2026 ConnectKard. All rights reserved.
            </p>
            
            <div className="flex gap-8 text-sm text-zinc-400 font-medium">
              <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
              <Link to="/features" className="hover:text-white transition-colors duration-300">Features</Link>
              <Link to="/contact" className="hover:text-white transition-colors duration-300">Contact</Link>
              <Link to="/admin/login" className="hover:text-white transition-colors duration-300">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;